import { Injectable, NgZone } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { ApplicationStates, LoginProvider, UISecurityProvider } from './auth-nz/interfaces';
import { environment } from '../../environments/environment';
import { BackendService } from '../services/backend.service';
import { InAppBrowser } from '@awesome-cordova-plugins/in-app-browser/ngx';
import {
  combineLatest,
  defer,
  Observable,
  ReplaySubject,
  Subscription,
} from 'rxjs';
import { TextableOrganization, TextableUser } from '../backported_types/users';
import { mapToTextableBaseFirestoreDocument } from '../backported_types/base';
import {
  catchError,
  distinctUntilChanged,
  distinctUntilKeyChanged,
  filter,
  map,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs/operators';
import { getLogger } from '@shared/logging';
import { BaseSecurityProvider } from './auth-nz/base';
import { sleep } from '../functions';
import { Platform } from '@ionic/angular';
import firebase from 'firebase/compat/app'
import { ProxyReceiveError, ProxyReceiveObject } from 'src/classes/PopupCrossOriginDTOProxy';
import { ActivatedRoute, Router } from '@angular/router';
import { URLTokenAuthProvider } from './auth-nz/providers/URLTokenAuthProvider';

const log = getLogger("AuthNZ")

export class AuthenticationError extends Error {
  constructor(public PrimaryMessage: string, public secondaryMessage?: string, public referenceCode?: string) {
    super(PrimaryMessage);
    this.name = "AuthenticationError";
  }
}


export type CustomToken = string;

@Injectable({ providedIn: 'root' })
export class AuthNZService {

  public isProcessingLogin$ = new ReplaySubject<boolean>(1);
  public SSOLoginErrors$ = new ReplaySubject<AuthenticationError>(1);
  public currentSecurityProvider:  UISecurityProvider

  private userProfileSubscription: Subscription;
  private userOrganizationSubscription: Subscription;
  private activeNumberProfileSubscription: Subscription;
  private activeNumberUID$ = new ReplaySubject<string>(1);

  /**
   * The currently active number; defaults to the logged in user at login, but
   * can be changed via the number switcher / picker
   */
  public activeNumberProfile$: Observable<TextableUser>;

  public loggedInUserProfile$ = new ReplaySubject<TextableUser>(1);
  public loggedInUserOrganization$ = new ReplaySubject<TextableOrganization>(1);

  /**
   * Events are published to this observable whenever the "Logged in state" of the application changes.
   * Available states are defined in the ApplicationStates ENUM, and include LoggedIn, LoggedOut, PreLogout, and PendingEmailVerification.
   *
   * The PendingEmailVerification state is only published when the current UISecurityProvider demands.
   *
   */
  public authState$ = new ReplaySubject<ApplicationStates>(1);

  /**
   * URL to which users will be redirected after logging out
   */
  public logoutURL: string;

  public preLogoutTasks: {():Promise<void>}[] = [];

  public authProviders = defer(async () => {
    return this.getLoginProviders();
  }).pipe(shareReplay(1));

  constructor(
    private afa: AngularFireAuth,
    private afs: AngularFirestore,
    private backendService: BackendService,
    private iab: InAppBrowser,
    private platform: Platform,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private ngZone: NgZone
  ) {
    /**
     * 
     * watch activated route for ProviderName query parameter
     */

    combineLatest([activatedRoute.queryParams, this.authProviders.pipe(
      map(ap=>{
        return ap.map(p=>{
          if (p.type == "urltoken") {
            return new URLTokenAuthProvider(p, this.backendService)
          }
        })
      })
    )]).subscribe(async ([params,providers]) => {
      if(params.ProviderId && params.token){
        this.isProcessingLogin$.next(true);
      }
      for (let prov of providers) {
        if (prov && prov.isLoginTrigger(params)){
          try {
            const customToken = await prov.signIn();
            await this.signInWithCustomToken(customToken)
          }
          catch (err){
            if (err instanceof AuthenticationError) {
              this.SSOLoginErrors$.next(err);
            }
            else {
              this.SSOLoginErrors$.next(new AuthenticationError("SSO Login Failed", "Please try again, or contact your administrator"));
            }
          }
          this.isProcessingLogin$.next(false);
        }
      }
    });

    this.activeNumberProfile$ = this.activeNumberUID$.pipe(
      distinctUntilChanged(),
      tap(uid=>{
        log.debug('Switching active number uid to: ' + uid)
      }),
      filter(uid=>{
        if(!uid) {
          log.warn("No active number uid provided.  Returning null");
          return false
        }
        return true;
      }),
      switchMap(uid =>
          this.afs
          .doc('users/' + uid)
          .snapshotChanges()
          .pipe(
            map(ud=>mapToTextableBaseFirestoreDocument<TextableUser>(ud.payload)),
            catchError((err)=>{
              throw new Error("Error fetching active number profile: " + err.message)
            })
          )
      ),
      tap((user) => {
        log.debug('Active number changed to: ', user);
      }),
      shareReplay(1)
    )
    this.currentSecurityProvider = new BaseSecurityProvider(this);
    this.afa.authState
      .pipe(
        filter((value) => {
          if (!value) {
            this.authState$.next(ApplicationStates.LoggedOut);
            return false;
          }
          return true;
        }),
        tap((u) => {
          // process non-user-changes here.
        }),
        distinctUntilKeyChanged('uid'),
        tap((user) => {
          log.debug('login user: ' + user.email);
          this.setActiveNumber(user.uid);
          this.setupProfileObservable(user.uid);
        })
      )
      .subscribe((user) => {
        if (user) {
          this.authState$.next(ApplicationStates.LoggedIn)
        }
      });
  }



  public async emailLogin(email, password) {
    await this.afa.signInWithEmailAndPassword(email, password);
    window.location.reload();
  }

  private async getLoginProviders(): Promise<LoginProvider[]> {
    let providers: LoginProvider[] = [];
    try {
      log.debug('getting auth providers');
      const authProvidersResponse = (await this.backendService.backendGet(
        'auth/providers',
        null,
        false
      )) as any;

      if (!authProvidersResponse || !Array.isArray(authProvidersResponse)) {
        log.debug('Error getting auth providers');
        throw new Error('Error getting auth providers');
      }

      authProvidersResponse.forEach((provider) => {
        providers.push({
          backendURL: provider.backendURL,
          name: provider.name,
          type: provider.type,
          color: provider.color,
          showLoginButton: provider.showLoginButton,
          id: provider.id,
        });
      });
      log.debug('fetched providers', providers);
      return providers;
    } catch (err) {
      log.error('Error fetching providers: ', err);
      return [];
    }
  }

  public async providerLogin(
    provider: LoginProvider,
    onSuccess: () => void,
    onError: (error: AuthenticationError) => void
  ) {
    this.platform.ready().then(async (v) => {
      log.debug('Platform ready', this.platform.platforms());
      const targetURL = `${environment.apiBase}${provider.backendURL}?ProviderName=${provider.name}`;
      try {
        const proxyResponse = await ProxyReceiveObject({
            targetURL: targetURL,
          },
          window, 
          this.iab
        );
        if (typeof proxyResponse.data.token != "string") {
          throw new AuthenticationError("Invalid token received from provider");
        }
        log.debug(`Logging in with SSO token: ${proxyResponse.data.token.substring(0, 10)}...`);
        await this.signInWithCustomToken(proxyResponse.data.token)
      }
      catch(err) {
        let realErr = err;
        if (realErr instanceof ProxyReceiveError) {
          realErr = new AuthenticationError("SSO Login Failed", realErr.UserFriendlyMessage.length>0 ? realErr.UserFriendlyMessage : "Please try again, or contact your administrator", err.referenceCode);
        }
        else if (err instanceof Error) {
          realErr = new AuthenticationError("SSO Login Failed", "Please try again, or contact your administrator");
        }
        log.warn(`Provider login error`, realErr);
        onError(realErr);
      }
    })
  }

  /**
   * Proxy function to sign in with a custom token.
   * @param token 
   */
    public async signInWithCustomToken(token: CustomToken) {
      try {
        const nt = await this.afa.signInWithCustomToken(token)
        const c = await firebase.auth().currentUser.getIdTokenResult();
        log.debug("Signed in with custom token", c)
        /**
         * TODO: We should be able to use router but it doesn't seem to work
         * so just reload:
         * 
         * 
         *  await this.router.navigateByUrl('/tabs/conversations');
         * 
         */
        location.reload();
      }
      catch(err){
        log.error("Error signing in with custom token: ", err);
      }
    }

  /**
   * Watch the currently logged in user's profile
   */
  private async setupProfileObservable(uid: string) {
    try {
      this.userProfileSubscription = this.afs
        .doc('users/' + uid)
        .snapshotChanges()
        .pipe(
          map((data) => {
            return mapToTextableBaseFirestoreDocument<TextableUser>(
              data.payload
            );
          }),
          map(u=>{
            u.disabledForBilling = false;
            if(!environment.isPrivateLabel){
              u.disabledForBilling = !['Active','PastDue'].includes(u.billing.status);
            }
            return u;
          }),
          filter((user) => {
            if (user.is_disabled) {
              this.logout();
              return false;
            }
            return true;
          })
        )
        .subscribe(this.loggedInUserProfile$);

      this.userOrganizationSubscription = this.loggedInUserProfile$
        .pipe(distinctUntilKeyChanged('organizationId'))
        .subscribe((user) => {
          this.afs
            .doc('organizations/' + user.organizationId)
            .snapshotChanges()
            .pipe(
              filter((od) => {
                if (!od.payload.exists) {
                  log.warn(
                    'User document is missing an organization.  This will be a stop-error in the future, but is currently permitted.'
                  );
                  return false;
                }
                return true;
              }),
              map((o) =>
                mapToTextableBaseFirestoreDocument<TextableOrganization>(
                  o.payload
                )
              )
            )
            .subscribe(this.loggedInUserOrganization$);
        });

      const latest = await combineLatest([
        this.loggedInUserProfile$,
        this.loggedInUserOrganization$,
      ]).toPromise();
      this.authState$.next(ApplicationStates.LoggedIn);
      this.setActiveNumber(latest[0].firebase_document_id);      
    } catch (err) {
      log.error('Error setting up profile watcher', err);
      this.authState$.next(ApplicationStates.Error);
    }
  }

  /**
   * Sets the active number to the user profile identified by the supplied uid
   * @param uid the user id to be used as the active number
   */
  public async setActiveNumber(uid: string) {
   
    this.activeNumberUID$.next(uid);
  }

  /**
   * Logs out the current user.   
   * Emits events on the authState$ observable so that listeners may gracefully clean up any resources requiring authentication
   */
  public async logout(skipRedirect: boolean = false) {
    log.debug("Preparing to logout")
    this.authState$.next(ApplicationStates.PreLogout);
    await sleep(200) // 
    log.debug("Waiting for pre-logout tasks to finish")
    await Promise.all(this.preLogoutTasks.map(t=>t()));
    this.preLogoutTasks = [];
    log.debug("Logging out")
    await this.afa.signOut();
    if (!skipRedirect) {
      if (typeof this.logoutURL == "string" && this.logoutURL.length > 0) {
        log.debug("Redirecting user to logoutURL: " + this.logoutURL)
        window.location.assign(this.logoutURL);
      }
      else {
        this.router.navigate(['login']);
        window.location.reload();
      }
    }
  }
}
